import React from 'react';
import { useLocation } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Grid,
  TextField,
  Button,
  IconButton,
} from '@mui/material';
import {
  Facebook,
  Twitter,
  Instagram,
  LinkedIn,
  YouTube,
  Email,
  Phone,
  LocationOn,
} from '@mui/icons-material';
import logo from '../assets/cropped_circle_image.png';
import OtherProducts from './OtherProducts';

const Footer = () => {
  const location = useLocation();
  const isAboutPage = location.pathname === '/about';

  const footerSections = [
    {
      title: 'For Members',
      links: [
        { text: 'How It Works', path: '/' },
        { text: 'Subscription Plans', path: '/' },
        { text: 'Member Benefits', path: '/' },
        { text: 'QR Code Guide', path: '/' },
      ],
    },
    {
      title: 'For Businesses',
      links: [
        { text: 'Partner with 10D', path: '/business' },
        { text: 'Partnership Benefits', path: '/business' },
        { text: 'How to Join', path: '/business' },
        { text: 'Business Dashboard', path: '/dashboard' },
        { text: 'Success Stories', path: '/business' },
      ],
    },
    {
      title: 'Company',
      links: [
        { text: 'About Us', path: '/about' },
        { text: 'Contact Us', path: '/contact' },
        { text: 'Careers', path: '/careers' },
        { text: 'Press Kit', path: '/press' },
      ],
    },
    {
      title: 'Support',
      links: [
        { text: 'Help Center', path: '/help' },
        { text: 'FAQs', path: '/faqs' },
        { text: 'Contact Support', path: '/contact' },
        { text: 'Terms of Service', path: '/terms' },
        { text: 'Privacy Policy', path: '/privacy' },
      ],
    },
  ];

  const socialLinks = [
    { icon: <Facebook />, path: 'https://facebook.com/10dstores', label: 'Facebook' },
    { icon: <Twitter />, path: 'https://twitter.com/10dstores', label: 'Twitter' },
    { icon: <Instagram />, path: 'https://instagram.com/10dstores', label: 'Instagram' },
    { icon: <LinkedIn />, path: 'https://linkedin.com/company/10dstores', label: 'LinkedIn' },
    { icon: <YouTube />, path: 'https://youtube.com/10dstores', label: 'YouTube' },
  ];

  return (
    <>
      {!isAboutPage && <OtherProducts />}
      <Box component="footer" sx={{ backgroundColor: '#1E293B', color: 'white', py: 6 }}>
        <Container maxWidth="lg">
          <Grid container spacing={4}>
            {/* Company Info */}
            <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <img
                  src={logo}
                  alt="10D Stores Logo"
                  style={{ height: '50px', width: 'auto', marginRight: '12px' }}
                />
                <Typography variant="h6" sx={{ fontWeight: 'bold', color: 'white' }}>
                  10D Stores
                </Typography>
              </Box>
              <Typography variant="body2" sx={{ mb: 2, opacity: 0.8, color: 'white' }}>
                Connecting customers with local businesses for exclusive member discounts and in-store shopping experiences.
                No delivery fees, no middlemen - just real savings on real products.
              </Typography>
              <Box sx={{ display: 'flex', gap: 1 }}>
                {socialLinks.map((social) => (
                  <IconButton
                    key={social.label}
                    component="a"
                    href={social.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{
                      color: 'white',
                      '&:hover': { backgroundColor: 'rgba(255,255,255,0.1)' },
                    }}
                    aria-label={social.label}
                  >
                    {social.icon}
                  </IconButton>
                ))}
              </Box>
            </Grid>

            {/* Footer Links */}
            {footerSections.map((section) => (
              <Grid item xs={12} sm={6} md={2} key={section.title}>
                <Typography variant="h6" sx={{ mb: 2, fontWeight: 'bold', color: 'white' }}>
                  {section.title}
                </Typography>
                <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
                  {section.links.map((link) => (
                    <Box component="li" key={link.text} sx={{ mb: 1 }}>
                      <Typography
                        component="a"
                        href={link.path}
                        sx={{
                          color: 'white',
                          textDecoration: 'none',
                          opacity: 0.8,
                          '&:hover': { opacity: 1, textDecoration: 'underline' },
                        }}
                      >
                        {link.text}
                      </Typography>
                    </Box>
                  ))}
                </Box>
              </Grid>
            ))}
          </Grid>

          {/* Contact Info */}
          <Box sx={{ mt: 4, pt: 4, borderTop: 1, borderColor: '#2D3748' }}>
            <Grid container spacing={3}>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Email sx={{ mr: 1, fontSize: 20, color: 'white' }} />
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    support@10DStores.in
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <Phone sx={{ mr: 1, fontSize: 20, color: 'white' }} />
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    +91-8977555077
                  </Typography>
                </Box>
              </Grid>
              <Grid item xs={12} sm={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                  <LocationOn sx={{ mr: 1, fontSize: 20, color: 'white' }} />
                  <Typography variant="body2" sx={{ color: 'white' }}>
                    NSIC EMDBP, ECIL, Hyderabad, Telangana, India - 500 062
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          {/* Bottom Footer */}
          <Box sx={{ mt: 4, pt: 3, borderTop: 1, borderColor: '#2D3748', textAlign: 'center' }}>
            <Typography variant="body2" sx={{ opacity: 0.7 }}>
              © 2025 10D Stores. All rights reserved. | Made with ❤️ for local businesses and communities.
            </Typography>
          </Box>

          {/* Newsletter Signup */}
          <Box sx={{ mt: 4, p: 3, backgroundColor: '#334155', borderRadius: 2 }}>
            <Typography variant="h6" sx={{ mb: 2, textAlign: 'center', color: 'white' }}>
              Stay Updated with 10D Stores
            </Typography>
            <Typography variant="body2" sx={{ mb: 3, textAlign: 'center', opacity: 0.8, color: 'white' }}>
              Get the latest updates on new business partners, exclusive offers, and member benefits.
            </Typography>
            <Box sx={{ display: 'flex', gap: 2, maxWidth: 500, mx: 'auto' }}>
              <TextField
                fullWidth
                placeholder="Enter your email address"
                variant="outlined"
                size="small"
                sx={{
                  '& .MuiOutlinedInput-root': {
                    color: 'white',
                    '& fieldset': { borderColor: '#4A5568' },
                    '&:hover fieldset': { borderColor: '#718096' },
                    '&.Mui-focused fieldset': { borderColor: 'primary.main' },
                  },
                  '& .MuiInputLabel-root': { color: 'grey.400' },
                  '& input::placeholder': { color: 'grey.400', opacity: 1 },
                }}
              />
              <Button
                variant="contained"
                sx={{
                  backgroundColor: '#10B981',
                  color: 'white',
                  '&:hover': { backgroundColor: '#059669' },
                  textTransform: 'none',
                  px: 3
                }}
              >
                Subscribe
              </Button>
            </Box>
          </Box>
        </Container>
      </Box>
    </>
  );
};

export default Footer;
